import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Loader2, CheckCircle2, XCircle, Server, Globe, Shield, CreditCard, ArrowRight, ArrowLeft, Lock, User, ShoppingBag, Trash2 } from 'lucide-react';
interface CheckoutFlowProps {
  initialStep?: 'search' | 'hosting';
  onClose: () => void;
  initialDomain?: string;
  initialHosting?: {
    name: string;
    price: number;
  };
}
type Step = 'search' | 'hosting' | 'auth' | 'payment' | 'success';
export function CheckoutFlow({
  initialStep = 'search',
  onClose,
  initialDomain,
  initialHosting
}: CheckoutFlowProps) {
  const [step, setStep] = useState<Step>(initialStep);
  const [domainQuery, setDomainQuery] = useState(initialDomain || '');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<{
    name: string;
    price: number;
  } | null>(initialDomain ? {
    name: initialDomain,
    price: 14.99
  } : null);
  const [selectedHosting, setSelectedHosting] = useState<{
    name: string;
    price: number;
  } | null>(initialHosting || null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const domains = [{
    suffix: '.com',
    price: 14.99,
    available: true
  }, {
    suffix: '.net',
    price: 12.99,
    available: true
  }, {
    suffix: '.org',
    price: 15.99,
    available: false
  }, {
    suffix: '.io',
    price: 49.99,
    available: true
  }, {
    suffix: '.me',
    price: 19.99,
    available: true
  }, {
    suffix: '.tv',
    price: 34.99,
    available: true
  }, {
    suffix: '.app',
    price: 24.99,
    available: true
  }];
  const hostingPlans = [{
    name: 'Starter Node',
    price: 29,
    desc: 'Perfect for emerging artists.'
  }, {
    name: 'Creator Core',
    price: 79,
    desc: 'Optimized for high-traffic portfolios.',
    popular: true
  }, {
    name: 'Empire Pro',
    price: 199,
    desc: 'Ultimate power for labels.'
  }];
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!domainQuery) return;
    setIsSearching(true);
    setTimeout(() => setIsSearching(false), 1000);
  };
  const handlePayment = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setStep('success');
    }, 2500);
  };
  const total = (selectedDomain?.price || 0) + (selectedHosting?.price || 0);
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 bg-black/90 backdrop-blur-sm overflow-y-auto" data-id="element-1">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-[#1A1A1A] border border-white/10 w-full max-w-4xl rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row min-h-[600px]" data-id="element-2">
        {/* Left Side: Flow Content */}
        <div className="flex-1 p-8 md:p-12 relative" data-id="element-3">
          <button onClick={onClose} className="absolute top-6 left-6 text-gray-500 hover:text-white transition-colors flex items-center gap-2 text-xs font-bold uppercase tracking-widest" data-id="element-4">
            <ArrowLeft size={14} data-id="element-5" /> Exit
          </button>

          <div className="mt-12" data-id="element-6">
            <AnimatePresence mode="wait" data-id="element-7">
              {step === 'search' && <motion.div key="search" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-8" data-id="element-8">
                  <div data-id="element-9">
                    <h2 className="font-['Archivo_Black'] text-3xl uppercase mb-2" data-id="element-10">
                      Find Your Domain
                    </h2>
                    <p className="text-gray-400" data-id="element-11">
                      Claim your piece of the internet empire.
                    </p>
                  </div>

                  <form onSubmit={handleSearch} className="flex gap-2" data-id="element-12">
                    <div className="flex-1 relative" data-id="element-13">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} data-id="element-14" />
                      <input type="text" value={domainQuery} onChange={e => setDomainQuery(e.target.value)} placeholder="yourbrandname" className="w-full bg-black/50 border border-white/10 rounded-xl py-4 pl-12 pr-4 focus:outline-none focus:border-[#FFD700] transition-colors font-bold" data-id="element-15" />
                    </div>
                    <button className="bg-[#FFD700] text-black px-8 rounded-xl font-bold uppercase tracking-widest hover:bg-white transition-all" data-id="element-16">
                      {isSearching ? <Loader2 className="animate-spin" data-id="element-17" /> : 'Search'}
                    </button>
                  </form>

                  {(domainQuery || initialDomain) && !isSearching && <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar" data-id="element-18">
                      {domains.map((d, i) => <div key={i} className="p-4 bg-black/40 rounded-xl border border-white/5 flex items-center justify-between group hover:border-white/20 transition-colors" data-id="element-19">
                          <div data-id="element-20">
                            <span className="font-bold text-lg" data-id="element-21">
                              {(domainQuery || initialDomain || '').split('.')[0]}
                            </span>
                            <span className="text-[#FFD700] font-bold text-lg" data-id="element-22">
                              {d.suffix}
                            </span>
                            <p className="text-[10px] uppercase font-bold tracking-widest text-gray-500" data-id="element-23">
                              {d.available ? 'Available' : 'Taken'}
                            </p>
                          </div>
                          <div className="flex items-center gap-4" data-id="element-24">
                            <span className="font-['Archivo_Black']" data-id="element-25">
                              ${d.price}
                            </span>
                            <button disabled={!d.available} onClick={() => {
                      setSelectedDomain({
                        name: (domainQuery || initialDomain || '').split('.')[0] + d.suffix,
                        price: d.price
                      });
                      setStep('hosting');
                    }} className="bg-white text-black px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-[#FFD700] transition-colors disabled:opacity-30" data-id="element-26">
                              Select
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  {!domainQuery && !initialDomain && <div className="py-12 text-center border-2 border-dashed border-white/5 rounded-2xl" data-id="element-27">
                      <Globe className="mx-auto mb-4 text-gray-700" size={48} data-id="element-28" />
                      <p className="text-gray-600 font-bold uppercase tracking-widest text-xs" data-id="element-29">
                        Enter a name to begin
                      </p>
                    </div>}
                </motion.div>}

              {step === 'hosting' && <motion.div key="hosting" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-8" data-id="element-30">
                  <div data-id="element-31">
                    <h2 className="font-['Archivo_Black'] text-3xl uppercase mb-2" data-id="element-32">
                      Add Pro Hosting
                    </h2>
                    <p className="text-gray-400" data-id="element-33">
                      Power your site with Niishcloud infrastructure.
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-34">
                    {hostingPlans.map((plan, i) => <div key={i} onClick={() => setSelectedHosting({
                  name: plan.name,
                  price: plan.price
                })} className={`p-6 rounded-2xl border cursor-pointer transition-all flex items-center justify-between ${selectedHosting?.name === plan.name ? 'bg-[#FFD700]/10 border-[#FFD700]' : 'bg-black/40 border-white/5 hover:border-white/20'}`} data-id="element-35">
                        <div className="flex items-center gap-4" data-id="element-36">
                          <div className={`p-3 rounded-xl ${selectedHosting?.name === plan.name ? 'bg-[#FFD700] text-black' : 'bg-white/5 text-gray-400'}`} data-id="element-37">
                            <Server size={24} data-id="element-38" />
                          </div>
                          <div data-id="element-39">
                            <h3 className="font-bold uppercase tracking-wider" data-id="element-40">
                              {plan.name}
                            </h3>
                            <p className="text-xs text-gray-500" data-id="element-41">{plan.desc}</p>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-42">
                          <p className="font-['Archivo_Black'] text-xl" data-id="element-43">
                            ${plan.price}
                            <span className="text-[10px] font-sans text-gray-500" data-id="element-44">
                              /mo
                            </span>
                          </p>
                          {plan.popular && <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-[#FFD700]" data-id="element-45">
                              Recommended
                            </span>}
                        </div>
                      </div>)}
                    <button onClick={() => {
                  setStep(isLoggedIn ? 'payment' : 'auth');
                }} className="w-full py-4 text-gray-500 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest" data-id="element-46">
                      Skip for now
                    </button>
                  </div>

                  <button disabled={!selectedHosting} onClick={() => setStep(isLoggedIn ? 'payment' : 'auth')} className="w-full bg-[#FFD700] text-black py-5 rounded-2xl font-bold uppercase tracking-widest hover:bg-white transition-all shadow-xl flex items-center justify-center gap-2 disabled:opacity-50" data-id="element-47">
                    Continue to Checkout <ArrowRight size={18} data-id="element-48" />
                  </button>
                </motion.div>}

              {step === 'auth' && <motion.div key="auth" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-8" data-id="element-49">
                  <div data-id="element-50">
                    <h2 className="font-['Archivo_Black'] text-3xl uppercase mb-2" data-id="element-51">
                      Creator Login
                    </h2>
                    <p className="text-gray-400" data-id="element-52">
                      Sign in to manage your empire.
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-53">
                    <div className="space-y-2" data-id="element-54">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 ml-1" data-id="element-55">
                        Email Address
                      </label>
                      <input type="email" defaultValue="artist@empire.com" className="w-full bg-black/50 border border-white/10 rounded-xl py-4 px-4 focus:outline-none focus:border-[#FFD700] transition-colors font-bold" data-id="element-56" />
                    </div>
                    <div className="space-y-2" data-id="element-57">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 ml-1" data-id="element-58">
                        Password
                      </label>
                      <input type="password" defaultValue="••••••••" className="w-full bg-black/50 border border-white/10 rounded-xl py-4 px-4 focus:outline-none focus:border-[#FFD700] transition-colors font-bold" data-id="element-59" />
                    </div>
                  </div>

                  <div className="space-y-4 pt-4" data-id="element-60">
                    <button onClick={() => {
                  setIsLoggedIn(true);
                  setStep('payment');
                }} className="w-full bg-[#FFD700] text-black py-5 rounded-2xl font-bold uppercase tracking-widest hover:bg-white transition-all shadow-xl" data-id="element-61">
                      Sign In & Continue
                    </button>
                    <button className="w-full border border-white/10 text-white py-5 rounded-2xl font-bold uppercase tracking-widest hover:bg-white/5 transition-all" data-id="element-62">
                      Create New Account
                    </button>
                  </div>
                </motion.div>}

              {step === 'payment' && <motion.div key="payment" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-8" data-id="element-63">
                  <div data-id="element-64">
                    <h2 className="font-['Archivo_Black'] text-3xl uppercase mb-2" data-id="element-65">
                      Secure Payment
                    </h2>
                    <p className="text-gray-400" data-id="element-66">
                      Encrypted checkout via NiishPay.
                    </p>
                  </div>

                  <div className="space-y-6" data-id="element-67">
                    <div className="p-6 bg-black/40 rounded-2xl border border-white/5 space-y-4" data-id="element-68">
                      <div className="flex items-center gap-4 border-b border-white/5 pb-4" data-id="element-69">
                        <CreditCard className="text-[#FFD700]" data-id="element-70" />
                        <span className="font-bold uppercase tracking-widest text-sm" data-id="element-71">
                          Credit or Debit Card
                        </span>
                      </div>
                      <div className="space-y-4" data-id="element-72">
                        <input type="text" placeholder="Card Number" defaultValue="4242 •••• •••• 4242" className="w-full bg-black/50 border border-white/10 rounded-xl py-3 px-4 focus:outline-none focus:border-[#FFD700] transition-colors font-bold" data-id="element-73" />
                        <div className="grid grid-cols-2 gap-4" data-id="element-74">
                          <input type="text" placeholder="MM/YY" defaultValue="12/26" className="w-full bg-black/50 border border-white/10 rounded-xl py-3 px-4 focus:outline-none focus:border-[#FFD700] transition-colors font-bold" data-id="element-75" />
                          <input type="text" placeholder="CVC" defaultValue="•••" className="w-full bg-black/50 border border-white/10 rounded-xl py-3 px-4 focus:outline-none focus:border-[#FFD700] transition-colors font-bold" data-id="element-76" />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase font-bold tracking-widest justify-center" data-id="element-77">
                      <Lock size={12} data-id="element-78" /> SSL Secure 256-bit Encryption
                    </div>

                    <button onClick={handlePayment} disabled={isProcessing} className="w-full bg-[#FFD700] text-black py-5 rounded-2xl font-bold uppercase tracking-widest hover:bg-white transition-all shadow-xl flex items-center justify-center gap-3" data-id="element-79">
                      {isProcessing ? <Loader2 className="animate-spin" data-id="element-80" /> : <>
                          Pay ${total.toFixed(2)} Now <ArrowRight size={18} data-id="element-81" />
                        </>}
                    </button>
                  </div>
                </motion.div>}

              {step === 'success' && <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-center py-12 space-y-8" data-id="element-82">
                  <div className="w-24 h-24 bg-emerald-500/20 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto" data-id="element-83">
                    <CheckCircle2 className="text-emerald-500" size={48} data-id="element-84" />
                  </div>
                  <div data-id="element-85">
                    <h2 className="font-['Archivo_Black'] text-4xl uppercase mb-4" data-id="element-86">
                      Empire Secured!
                    </h2>
                    <p className="text-gray-400 max-w-sm mx-auto" data-id="element-87">
                      Your domain and hosting have been provisioned. Welcome to
                      the elite.
                    </p>
                  </div>
                  <div className="p-6 bg-black/40 rounded-2xl border border-white/5 text-left space-y-2" data-id="element-88">
                    <p className="text-[10px] font-bold uppercase text-gray-500 tracking-widest" data-id="element-89">
                      Order Summary
                    </p>
                    <p className="text-sm font-bold flex justify-between" data-id="element-90">
                      <span data-id="element-91">Domain:</span>{' '}
                      <span className="text-[#FFD700]" data-id="element-92">
                        {selectedDomain?.name}
                      </span>
                    </p>
                    {selectedHosting && <p className="text-sm font-bold flex justify-between" data-id="element-93">
                        <span data-id="element-94">Hosting:</span>{' '}
                        <span className="text-[#FFD700]" data-id="element-95">
                          {selectedHosting.name}
                        </span>
                      </p>}
                  </div>
                  <button onClick={onClose} className="w-full bg-white text-black py-5 rounded-2xl font-bold uppercase tracking-widest hover:bg-[#FFD700] transition-all shadow-xl" data-id="element-96">
                    Enter Creator Dashboard
                  </button>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* Right Side: Cart Summary */}
        <div className="w-full md:w-80 bg-black/40 border-l border-white/10 p-8 flex flex-col" data-id="element-97">
          <div className="flex items-center gap-3 mb-12" data-id="element-98">
            <ShoppingBag className="text-[#FFD700]" size={20} data-id="element-99" />
            <h3 className="font-bold uppercase tracking-widest text-sm" data-id="element-100">
              Order Summary
            </h3>
          </div>

          <div className="flex-1 space-y-6" data-id="element-101">
            {selectedDomain ? <div className="space-y-2" data-id="element-102">
                <div className="flex justify-between items-start" data-id="element-103">
                  <div data-id="element-104">
                    <p className="text-xs font-bold uppercase text-gray-500 tracking-widest" data-id="element-105">
                      Domain
                    </p>
                    <p className="font-bold text-sm" data-id="element-106">{selectedDomain.name}</p>
                  </div>
                  <button onClick={() => setSelectedDomain(null)} className="text-gray-600 hover:text-red-500 transition-colors" data-id="element-107">
                    <Trash2 size={14} data-id="element-108" />
                  </button>
                </div>
                <p className="text-right font-['Archivo_Black'] text-sm" data-id="element-109">
                  ${selectedDomain.price}
                </p>
              </div> : <p className="text-xs text-gray-600 italic" data-id="element-110">No domain selected</p>}

            {selectedHosting ? <div className="space-y-2 pt-6 border-t border-white/5" data-id="element-111">
                <div className="flex justify-between items-start" data-id="element-112">
                  <div data-id="element-113">
                    <p className="text-xs font-bold uppercase text-gray-500 tracking-widest" data-id="element-114">
                      Hosting
                    </p>
                    <p className="font-bold text-sm" data-id="element-115">{selectedHosting.name}</p>
                  </div>
                  <button onClick={() => setSelectedHosting(null)} className="text-gray-600 hover:text-red-500 transition-colors" data-id="element-116">
                    <Trash2 size={14} data-id="element-117" />
                  </button>
                </div>
                <p className="text-right font-['Archivo_Black'] text-sm" data-id="element-118">
                  ${selectedHosting.price}
                  <span className="text-[10px] font-sans text-gray-500" data-id="element-119">
                    /mo
                  </span>
                </p>
              </div> : <div className="pt-6 border-t border-white/5" data-id="element-120">
                <p className="text-xs text-gray-600 italic" data-id="element-121">No hosting plan</p>
              </div>}
          </div>

          <div className="mt-12 pt-6 border-t-2 border-white/10 space-y-4" data-id="element-122">
            <div className="flex justify-between items-center" data-id="element-123">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-500" data-id="element-124">
                Total Due
              </span>
              <span className="text-2xl font-['Archivo_Black'] text-[#FFD700]" data-id="element-125">
                ${total.toFixed(2)}
              </span>
            </div>
            <p className="text-[10px] text-gray-600 leading-relaxed" data-id="element-126">
              * Domain renewals are billed annually. Hosting billed monthly.
            </p>
          </div>
        </div>
      </motion.div>
    </div>;
}